import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Block } from '@/types';
import { inferQuotePageBrand, resolveAgencyLogoUrl } from '@/lib/quoteBranding';

const BLOCK_TYPES_WITH_CLIENT_LOGO = [
  'COVER',
  'CONTENT_PAGE',
  'TEAM',
  'INVESTMENT',
  'PLAN_VAN_AANPAK',
  'SIGNATURE_PAGE',
] as const;

export function getQuoteClientLogo(
  blocks: Block[],
  layoutConfig?: Record<string, unknown> | null,
): string | undefined {
  const block = blocks.find((b) => {
    if (!BLOCK_TYPES_WITH_CLIENT_LOGO.includes(b.type as (typeof BLOCK_TYPES_WITH_CLIENT_LOGO)[number])) return false;
    const content = b.content as { clientLogoUrl?: string };
    return !!content?.clientLogoUrl;
  });
  if (block) return (block.content as { clientLogoUrl?: string })?.clientLogoUrl;
  const inherited = layoutConfig?.clientLogoUrl;
  return typeof inherited === 'string' && inherited.trim() ? inherited : undefined;
}

export function blockTypeSupportsClientLogo(type: string): boolean {
  return BLOCK_TYPES_WITH_CLIENT_LOGO.includes(type as (typeof BLOCK_TYPES_WITH_CLIENT_LOGO)[number]);
}

interface QuoteClientLogoContextValue {
  quoteClientLogoUrl: string | undefined;
  quotePageBrand: 'ubo' | 'ubx';
  /** layoutConfig.clientLogoWhiteFilterOnDark — standaard true (wit klantlogo op donkere header). */
  clientLogoWhiteFilterOnDark: boolean;
}

const QuoteClientLogoContext = createContext<QuoteClientLogoContextValue>({
  quoteClientLogoUrl: undefined,
  quotePageBrand: 'ubo',
  clientLogoWhiteFilterOnDark: true,
});

export function QuoteClientLogoProvider({
  blocks,
  layoutConfig,
  children,
}: {
  blocks: Block[];
  /** Voor inferQuotePageBrand (UBX-preset). */
  layoutConfig?: Record<string, unknown> | null;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      quoteClientLogoUrl: getQuoteClientLogo(blocks, layoutConfig),
      quotePageBrand: inferQuotePageBrand(blocks, layoutConfig ?? null),
      clientLogoWhiteFilterOnDark: layoutConfig?.clientLogoWhiteFilterOnDark !== false,
    }),
    [blocks, layoutConfig]
  );
  return (
    <QuoteClientLogoContext.Provider value={value}>
      {children}
    </QuoteClientLogoContext.Provider>
  );
}

export function useQuoteClientLogo(): string | undefined {
  return useContext(QuoteClientLogoContext).quoteClientLogoUrl;
}

/** UBX vs UBO voor PageHeader / TipTap (zelfde als blokken met theme). */
export function useQuotePageBrand(): 'ubo' | 'ubx' {
  return useContext(QuoteClientLogoContext).quotePageBrand;
}

/** Wit filter op klantlogo bij donkere header; uit te zetten via layoutConfig (toolbar). */
export function useClientLogoWhiteFilterOnDark(): boolean {
  return useContext(QuoteClientLogoContext).clientLogoWhiteFilterOnDark;
}

/**
 * Agency-logo met theme-fallback: `blockTheme` (per blok) of anders quote-breed merk.
 */
export function useResolvedAgencyLogoUrl(
  stored: string | null | undefined,
  blockTheme?: 'ubo' | 'ubx'
): string {
  const quoteBrand = useQuotePageBrand();
  const theme = blockTheme ?? quoteBrand;
  return resolveAgencyLogoUrl(stored, theme);
}
